import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { Button, Card, EmptyState, ErrorState, IconButton, PageHeader, Select, Skeleton } from '@liftango/ui-elements';
import { Icon } from '@liftango/ui-icons';
import { opsColors, opsFontSize, opsSpace } from '../../../../../theme/opsTokens';
import { simulationService } from '../../../../../services/simulation/simulationService';
import { parseResultTime } from '../../../../../services/simulation/runReport';
import type { SimulationRunSummary, UnmatchedRide } from '../../../../../types/simulation';
import { useRunReport, type ReportSource, type RunReport } from '../useRunReport';
import { buildKpis } from './kpis';
import { KpiStrip, type LoadStatus } from './KpiStrip';
import { PlaybackBar } from './PlaybackBar';
import { ReportCharts } from './ReportCharts';
import { ReportMap, type MapMode } from './ReportMap';
import { ResultsRail } from './ResultsRail';
import { usePlayback } from './usePlayback';
import { VehicleGantt } from './VehicleGantt';
import { formatDateTime, formatNumber } from './reportFormat';

type RunList =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly runs: readonly SimulationRunSummary[] }
  | { readonly status: 'error' };

const NO_COMPARISON = 'none';
const RAIL_WIDTH = '380px';
const SELECT_MIN_WIDTH = '220px';
const SELECT_MAX_WIDTH = '560px';
const SELECT_SKELETON = { width: SELECT_MIN_WIDTH, height: '34px' } as const;
const ICON_SIZE = 16;
const NO_UNMATCHED: readonly UnmatchedRide[] = [];
const NONE_HIDDEN: ReadonlySet<string> = new Set();
const RELOAD_SOURCES: readonly ReportSource[] = ['metrics', 'activities', 'unmatched', 'vehicles', 'compareMetrics', 'compareActivities', 'compareUnmatched', 'compareVehicles'];

/** A reload keeps the previous list on screen until the new one arrives. */
function useRunList(attempt: number): RunList {
  const [list, setList] = useState<RunList>({ status: 'loading' });
  useEffect(() => {
    let cancelled = false;
    setList((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }));
    simulationService.fetchMyResults().then(
      (runs) => {
        if (!cancelled) setList({ status: 'ready', runs });
      },
      () => {
        if (!cancelled) setList({ status: 'error' });
      }
    );
    return () => {
      cancelled = true;
    };
  }, [attempt]);
  return list;
}

function statusOf(...states: ReadonlyArray<RunReport[ReportSource]>): LoadStatus {
  if (states.some((state) => state.status === 'error')) return 'error';
  if (states.every((state) => state.status === 'ready')) return 'ready';
  return states.every((state) => state.status === 'idle') ? 'idle' : 'loading';
}

export function RunReportPage() {
  const { t, i18n } = useTranslation('simulation');
  const locale = i18n.language;
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [listAttempt, setListAttempt] = useState(0);
  const [selection, setSelection] = useState<{ runId: string; shiftId: string } | null>(null);
  const runList = useRunList(listAttempt);
  const runs = useMemo(() => (runList.status === 'ready' ? runList.runs : []), [runList]);
  const linkedRunId = params.get('run');
  const runId = linkedRunId ?? runs[0]?.run_id ?? null;
  const linkedCompare = params.get('compare');
  const compareRunId = linkedCompare && linkedCompare !== runId ? linkedCompare : null;
  const report = useRunReport(runId, compareRunId);
  const selectedShiftId = selection && selection.runId === runId ? selection.shiftId : null;
  const selectShift = useCallback((shiftId: string | null) => setSelection(shiftId && runId ? { runId, shiftId } : null), [runId]);
  const [hidden, setHidden] = useState<{ runId: string; shiftIds: ReadonlySet<string> } | null>(null);
  const hiddenShiftIds = hidden && hidden.runId === runId ? hidden.shiftIds : NONE_HIDDEN;
  const toggleShown = useCallback(
    (shiftId: string) => {
      if (!runId) return;
      const next = new Set(hiddenShiftIds);
      if (!next.delete(shiftId)) next.add(shiftId);
      setHidden({ runId, shiftIds: next });
    },
    [runId, hiddenShiftIds]
  );
  const [mapMode, setMapMode] = useState<MapMode>('time');
  const playback = usePlayback(runId, report.derived?.day ?? null);
  const { seek } = playback;
  const jumpTo = useCallback(
    (timeMs: number) => {
      setMapMode('time');
      seek(timeMs);
    },
    [seek]
  );

  const runLabel = useCallback(
    (run: SimulationRunSummary) => {
      const ranAt = parseResultTime(run.run_time);
      return t('results.report.header.runOption', {
        name: run.scenario_name,
        date: ranAt == null ? t('results.report.noValue') : formatDateTime(ranAt, locale),
        requests: formatNumber(run.num_requests ?? run.no_rides ?? 0, locale),
        vehicles: formatNumber(run.no_vehicles ?? 0, locale),
      });
    },
    [locale, t]
  );
  const runOptions = useMemo(() => runs.map((run) => ({ value: run.run_id, label: runLabel(run) })), [runs, runLabel]);

  const selectRun = (key: 'run' | 'compare', value: string) => {
    const next = new URLSearchParams(params);
    if (value && value !== NO_COMPARISON) next.set(key, value);
    else next.delete(key);
    if (key === 'run' && next.get('compare') === value) next.delete('compare');
    setParams(next);
  };

  const reload = () => {
    setListAttempt((attempt) => attempt + 1);
    RELOAD_SOURCES.forEach(report.retry);
    if (report.paths.status === 'error') report.retry('paths');
  };

  const metrics = report.metrics.status === 'ready' ? report.metrics.data : null;
  const compareMetrics = report.compareMetrics.status === 'ready' ? report.compareMetrics.data : null;
  const kpis = useMemo(() => buildKpis(metrics, compareMetrics, report.derived), [metrics, compareMetrics, report.derived]);

  if (!linkedRunId && runList.status === 'error') {
    return (
      <PageShell>
        <Card>
          <ErrorState title={t('results.report.errors.runs')} onRetry={() => setListAttempt((attempt) => attempt + 1)} retryLabel={t('results.report.retry')} />
        </Card>
      </PageShell>
    );
  }
  if (!linkedRunId && runList.status === 'ready' && runs.length === 0) {
    return (
      <PageShell>
        <Card>
          <EmptyState title={t('results.sidebar.noResults')} />
        </Card>
      </PageShell>
    );
  }

  const current = runs.find((run) => run.run_id === runId);
  const compare = runs.find((run) => run.run_id === compareRunId);
  const ranAt = parseResultTime(current?.run_time ?? metrics?.run_time);
  const derivedFailedSource: ReportSource | null =
    report.activities.status === 'error' ? 'activities' : report.vehicles.status === 'error' ? 'vehicles' : null;

  return (
    <PageShell>
      <PageHeader
        variant="content"
        eyebrow={ranAt == null ? t('results.report.header.eyebrow') : t('results.report.header.eyebrowWithRun', { date: formatDateTime(ranAt, locale) })}
        title={current?.scenario_name ?? metrics?.scenario_name ?? t('results.report.header.untitled')}
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: opsSpace[3] }}>
            <IconButton
              data-testid="sim-results.report.action.reload"
              variant="bordered"
              label={t('results.report.header.reload')}
              icon={<Icon name="arrows-rotate" size={ICON_SIZE} />}
              onClick={reload}
            />
            <Button data-testid="sim-results.report.action.back-to-map" icon={<Icon name="arrow-left" size={ICON_SIZE} />} onClick={() => navigate('/')}>
              {t('results.report.header.backToMap')}
            </Button>
          </Box>
        }
      />
      {/* Own row: full run labels are long, and in the header's action slot they squeeze the title. */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: opsSpace[3], flexWrap: 'wrap' }}>
        {runList.status === 'error' ? (
          <Box data-testid="sim-results.report.header.runs-error">
            <ErrorState title={t('results.report.errors.runs')} onRetry={() => setListAttempt((attempt) => attempt + 1)} retryLabel={t('results.report.retry')} />
          </Box>
        ) : (
          <>
            <LabelledSelect label={t('results.report.header.run')} testId="sim-results.report.header.run">
              {runList.status === 'loading' ? (
                <Skeleton {...SELECT_SKELETON} aria-label={t('results.report.loading')} />
              ) : (
                <Select aria-label={t('results.report.header.run')} options={runOptions} value={runId ?? ''} onChange={(value) => selectRun('run', value)} />
              )}
            </LabelledSelect>
            <LabelledSelect label={t('results.report.header.compare')} testId="sim-results.report.header.compare">
              <Select
                aria-label={t('results.report.header.compare')}
                disabled={runList.status === 'loading'}
                options={[{ value: NO_COMPARISON, label: t('results.report.header.noComparison') }, ...runOptions.filter((option) => option.value !== runId)]}
                value={compareRunId ?? NO_COMPARISON}
                onChange={(value) => selectRun('compare', value)}
              />
            </LabelledSelect>
          </>
        )}
      </Box>
      <KpiStrip
        kpis={kpis}
        metricsStatus={statusOf(report.metrics)}
        derivedStatus={report.derived ? 'ready' : statusOf(report.activities, report.vehicles)}
        derivedFailedSource={derivedFailedSource}
        compareStatus={statusOf(report.compareMetrics)}
        compareLabel={compare ? runLabel(compare) : null}
        onRetry={report.retry}
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: `minmax(0, 1fr) ${RAIL_WIDTH}`, gap: opsSpace[5], alignItems: 'stretch' }}>
        <ReportMap
          report={report}
          playback={playback}
          mode={mapMode}
          onModeChange={setMapMode}
          selectedShiftId={selectedShiftId}
          hiddenShiftIds={hiddenShiftIds}
          fitKey={runId ?? ''}
        >
          {report.derived?.day ? (
            <PlaybackBar
              day={report.derived.day}
              rides={report.derived.rides}
              unmatched={report.unmatched.status === 'ready' ? report.unmatched.data : NO_UNMATCHED}
              playback={playback}
            />
          ) : null}
        </ReportMap>
        <Box sx={{ position: 'relative', minHeight: 0 }}>
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex' }}>
            <ResultsRail
              report={report}
              selectedShiftId={selectedShiftId}
              onSelectShift={selectShift}
              onJumpTo={jumpTo}
              hiddenShiftIds={hiddenShiftIds}
              onToggleShown={toggleShown}
            />
          </Box>
        </Box>
      </Box>
      <VehicleGantt report={report} selectedShiftId={selectedShiftId} onSelectShift={selectShift} nowMs={mapMode === 'time' ? playback.timeMs : null} />
      <ReportCharts report={report} compareLabel={compare ? runLabel(compare) : null} />
    </PageShell>
  );
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <Box
      data-testid="sim-results.report.root"
      sx={{ minHeight: '100%', backgroundColor: opsColors.neutral.bg, p: opsSpace[6], display: 'grid', gap: opsSpace[5], alignContent: 'start' }}
    >
      {children}
    </Box>
  );
}

function LabelledSelect({ label, testId, children }: { label: string; testId: string; children: React.ReactNode }) {
  return (
    <Box
      data-testid={testId}
      sx={{
        display: 'grid',
        gap: opsSpace[1],
        minWidth: SELECT_MIN_WIDTH,
        maxWidth: SELECT_MAX_WIDTH,
        flex: '1 1 0',
        // A long run label stays on one line and truncates; the open list shows it in full.
        '& button': { textAlign: 'left' },
        '& button > span:first-of-type': { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
      }}
    >
      <Box component="span" sx={{ fontSize: opsFontSize.meta, color: opsColors.neutral.inkMute }}>
        {label}
      </Box>
      {children}
    </Box>
  );
}
